// Copyright Epic Games, Inc. All Rights Reserved.
import { useRouter } from 'next/router';

import { UserGatingProvider } from '@contexts/UserGating';
import { ProtectedContentProvider } from '@contexts/ProtectedContent';
import { checkIsAccessDenied, createProtectedRedirectUrl } from '@utils/user-gating';
import { APP_BASE_PATH } from '@constants/app';
import type { LocalesUnion } from '@constants/i18n';

import { useProtectedContentQuery } from './useProtectedContentQuery';

interface UserFlagsGatingWrapperProps {
  flags: string[];
  slug: string[];
  locale: LocalesUnion;
  children: React.ReactElement;
}

// The server-side endpoint reads protectedGroups.flags directly from the document; the
// wrapper passes the flag list only so the denial page can pick audience-appropriate
// copy. A denial returns a bare 403, so when a document declares several flags we
// cannot tell which one rejected — attribute it to the first, mirroring how the
// permissions path carries a single instructionId.
export const UserFlagsGatingWrapper: React.FC<UserFlagsGatingWrapperProps> = ({ children, flags, slug, locale }) => {
  const { asPath, push } = useRouter();
  const { data, error } = useProtectedContentQuery(slug, locale);

  if (error) {
    if (checkIsAccessDenied(error.response.status)) {
      push(createProtectedRedirectUrl(`/${APP_BASE_PATH}`, asPath, flags[0]));
    } else {
      push('/500');
    }
  }

  return (
    <UserGatingProvider userGatingStatus={{ isLoading: !data && !error, isError: !!error }}>
      <ProtectedContentProvider value={data ?? null}>{children}</ProtectedContentProvider>
    </UserGatingProvider>
  );
};
